import { useState, useEffect, useCallback } from "react";
import { Grip, X, ChevronLeft, ChevronRight } from "lucide-react";

const FALLBACK = "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const onErr = (e) => { e.currentTarget.src = FALLBACK; };

// Controlled full-screen lightbox with prev/next + thumbnails.
export function Lightbox({ images = [], startIndex = 0, open, onClose, title = "", testId = "lightbox" }) {
  const pics = images.length > 0 ? images : [FALLBACK];
  const [index, setIndex] = useState(startIndex);

  useEffect(() => { if (open) setIndex(startIndex); }, [open, startIndex]);

  const prev = useCallback(() => setIndex((i) => (i - 1 + pics.length) % pics.length), [pics.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % pics.length), [pics.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose, prev, next]);

  if (!open) return null;

  return (
    <div data-testid={testId} className="fixed inset-0 z-[100] bg-black flex flex-col animate-in fade-in duration-200">
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 text-white">
        <button data-testid={`${testId}-close`} onClick={onClose} className="inline-flex items-center gap-1.5 hover:opacity-80 text-sm font-medium">
          <X className="w-5 h-5" /> Close
        </button>
        <span className="text-sm text-white/80" data-testid={`${testId}-counter`}>{index + 1} / {pics.length}</span>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-16 relative min-h-0">
        {pics.length > 1 && (
          <button data-testid={`${testId}-prev`} onClick={prev} className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[#222] flex items-center justify-center shadow-lg transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <img src={pics[index]} alt={`${title} ${index + 1}`} onError={onErr} className="max-h-[76vh] max-w-full object-contain rounded-lg" />
        {pics.length > 1 && (
          <button data-testid={`${testId}-next`} onClick={next} className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[#222] flex items-center justify-center shadow-lg transition-colors">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {pics.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 sm:px-6 py-4 justify-start sm:justify-center">
          {pics.map((src, i) => (
            <button key={i} data-testid={`${testId}-thumb-${i}`} onClick={() => setIndex(i)} className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={src} alt="" onError={onErr} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Airbnb/Booking-style photo mosaic that opens the lightbox.
// `images` should be an array of ready-to-use URLs.
export function PhotoGallery({ images = [], title = "", testId = "photo-gallery" }) {
  const pics = images.length > 0 ? images : [FALLBACK];
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(0);
  const show = (i) => { setStart(i); setOpen(true); };
  const grid = pics.slice(0, 5);

  return (
    <div data-testid={testId}>
      <div className="relative grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 rounded-2xl overflow-hidden aspect-[16/10] md:aspect-[2/1] mb-8">
        <button type="button" data-testid={`${testId}-cover`} onClick={() => show(0)} className="md:col-span-2 md:row-span-2 w-full h-full overflow-hidden group">
          <img src={grid[0] || FALLBACK} alt={title} onError={onErr} className="w-full h-full object-cover group-hover:brightness-95 transition-all" />
        </button>
        {[1, 2, 3, 4].map((i) => (
          <button type="button" key={i} data-testid={`${testId}-tile-${i}`} onClick={() => show(Math.min(i, pics.length - 1))} className="hidden md:block w-full h-full overflow-hidden group">
            <img src={grid[i] || grid[0] || FALLBACK} alt="" onError={onErr} className="w-full h-full object-cover group-hover:brightness-95 transition-all" />
          </button>
        ))}
        <button
          type="button"
          data-testid={`${testId}-show-all`}
          onClick={() => show(0)}
          className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-white text-[#222] text-sm font-semibold px-4 py-2 rounded-lg shadow-md border border-[#222]/10 hover:scale-[1.03] active:scale-95 transition-transform"
        >
          <Grip className="w-4 h-4" /> Show all photos
        </button>
      </div>

      <Lightbox images={pics} startIndex={start} open={open} onClose={() => setOpen(false)} title={title} testId={`${testId}-lightbox`} />
    </div>
  );
}

export default PhotoGallery;
